'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PolicyItem {
  title: string;
  content: string;
  details: string[];
}

export default function BookingTermsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const bookingTerms: PolicyItem[] = [
    {
      title: 'Reservation Requirements',
      content: 'All reservations must be made by guests aged 18 years or older with valid government-issued identification.',
      details: [
        'Valid photo ID (Passport, Driving License, or Aadhaar Card) required at check-in',
        'Credit/debit card in guest name for security deposit authorization',
        'Corporate bookings require company authorization letter',
        'Advance booking recommended, especially during peak seasons',
      ],
    },
    {
      title: 'Check-in & Check-out Times',
      content: 'Standard check-in time is 11:00 AM and check-out time is 10:00 AM. Early check-in and late check-out subject to availability.',
      details: [
        'Early check-in (before 11:00 AM): 50% of daily rate, subject to availability',
        'Late check-out (10:00 AM - 6:00 PM): 50% of daily rate',
        'Late check-out (after 6:00 PM): Full day rate applies',
        'Express check-in/check-out available for corporate accounts',
      ],
    },
    {
      title: 'Occupancy Limits',
      content: 'Maximum occupancy limits are strictly enforced for safety and comfort compliance.',
      details: [
        'Luxos Luxury: Maximum 2 adults + 1 child (under 6 years)',
        'Primera Premium: Maximum 2 adults + 1 child (under 6 years)',
        'Classica Comfort: Maximum 2 adults + 1 child (under 6 years)',
        'Additional guests require prior approval and extra charges apply',
        'Infant (under 2 years) stays complimentary with existing occupants',
      ],
    },
    {
      title: 'Age Restrictions',
      content: 'Primary guest must be 18 years or older. Unaccompanied minors are not permitted.',
      details: [
        'All guests must be 18+ years old to make reservations',
        'Minors (under 18) must be accompanied by parent or legal guardian',
        'Valid age proof required at check-in for all guests',
        'Corporate bookings follow standard age verification procedures',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="booking-terms" className="py-16 lg:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="ClipboardDocumentCheckIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Booking Terms</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Reservation Policies
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Essential terms covering reservation requirements, check-in procedures, and occupancy limits for both apartment categories.
          </p>
        </div>

        <div className="space-y-4">
          {bookingTerms.map((term, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {term.title}
                  </h3>
                  <p className="text-text-secondary text-sm">{term.content}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {term.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}